import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  DollarSign, Download, Calendar, FileText, Eye, X, Loader2, TrendingUp, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import BackButton from '../../components/BackButton';

// Year range from 2020 to current year (2026)
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

// Professional color palette for years
const YEAR_COLORS = {
  2026: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  2025: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  2024: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  2023: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  2022: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  2021: { gradient: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  2020: { gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-500', light: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
};

const getYearColor = (year) => YEAR_COLORS[year] || YEAR_COLORS[2020];

const FinancialStatements = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStatement, setPreviewStatement] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => { loadStatements(); }, []);

  const loadStatements = async () => {
    try {
      const response = await api.get('/financial-statements');
      setStatements(response.data);
    } catch (error) {
      toast.error('Failed to load financial statements');
    } finally {
      setLoading(false);
    }
  };

  // Group statements by year
  const groupedStatements = useMemo(() => {
    const filtered = selectedYear === 'all' ? statements : statements.filter(s => s.year === parseInt(selectedYear));
    return filtered.reduce((acc, statement) => {
      const year = statement.year;
      if (!acc[year]) acc[year] = [];
      acc[year].push(statement);
      return acc;
    }, {});
  }, [statements, selectedYear]);

  // Stats
  const stats = useMemo(() => ({
    total: statements.length,
    years: [...new Set(statements.map(s => s.year))].length,
    thisYear: statements.filter(s => s.year === 2026).length,
  }), [statements]);

  const openPreview = async (statement) => {
    setPreviewStatement(statement);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(statement.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewStatement(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (statement) => {
    try {
      await downloadFile(statement.file_url, statement.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="member-financial-page">
      <BackButton to="/member" />
      
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-4 rounded-2xl shadow-lg">
          <DollarSign className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Financial Statements</h1>
          <p className="text-slate-600">Transparent financial records from 2020 onwards</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-600 text-sm font-medium">Total Statements</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-emerald-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Years Covered</p>
              <p className="text-2xl font-bold text-slate-900">{stats.years}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-600 text-sm font-medium">This Year (2026)</p>
              <p className="text-2xl font-bold text-slate-900">{stats.thisYear}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-violet-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-600 text-sm font-medium">Transparency</p>
              <p className="text-2xl font-bold text-slate-900">100%</p>
            </div>
            <BarChart3 className="w-8 h-8 text-amber-300" />
          </div>
        </div>
      </div>

      {/* Year Filter */}
      <div className="flex flex-wrap gap-2 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <span className="text-slate-500 text-sm self-center mr-2">Filter by Year:</span>
        <button onClick={() => setSelectedYear('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === 'all' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All Years</button>
        {YEARS.map(y => {
          const colors = getYearColor(y);
          return (
            <button key={y} onClick={() => setSelectedYear(y.toString())} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === y.toString() ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{y}</button>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewOpen && previewStatement && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-slate-900">{previewStatement.title}</h3>
                <p className="text-sm text-slate-500">{previewStatement.year} {previewStatement.quarter && `• ${previewStatement.quarter}`}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewStatement)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-50">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Year-based Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : Object.keys(groupedStatements).length > 0 ? (
        <div className="space-y-8">
          {Object.keys(groupedStatements).sort((a, b) => b - a).map((yearKey) => {
            const yearNum = parseInt(yearKey);
            const colors = getYearColor(yearNum);
            return (
              <div key={yearKey}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`bg-gradient-to-r ${colors.gradient} px-4 py-2 rounded-xl shadow-lg`}>
                    <span className="text-white font-bold text-lg">{yearKey}</span>
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
                  <span className="text-slate-500 text-sm">{groupedStatements[yearKey].length} document(s)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedStatements[yearKey].map((statement) => (
                    <div key={statement.id} className={`group bg-white rounded-2xl overflow-hidden border-2 ${colors.border} hover:shadow-xl transition-all duration-300`}>
                      <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`${colors.light} p-3 rounded-xl`}>
                            <DollarSign className={`w-6 h-6 ${colors.text}`} />
                          </div>
                        </div>
                        <h3 className={`font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:${colors.text} transition-colors`}>{statement.title}</h3>
                        <div className="flex items-center space-x-2 text-sm text-slate-500 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{statement.year}</span>
                          {statement.quarter && <span className={`${colors.bg} text-white px-2 py-0.5 rounded text-xs font-medium`}>{statement.quarter}</span>}
                        </div>
                        <div className="flex space-x-2">
                          <Button onClick={() => openPreview(statement)} variant="outline" size="sm" className="flex-1">
                            <Eye className="w-4 h-4 mr-1" />Preview
                          </Button>
                          <Button onClick={() => handleDownload(statement)} size="sm" className={`flex-1 bg-gradient-to-r ${colors.gradient} text-white`}>
                            <Download className="w-4 h-4 mr-1" />Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16">
          <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 text-lg">{selectedYear === 'all' ? 'No financial statements available yet' : `No statements for ${selectedYear}`}</p>
          <p className="text-sm text-slate-500 mt-2">Check back later for financial documents</p>
        </div>
      )}
    </div>
  );
};

export default FinancialStatements;
